import { useTranslation } from 'react-i18next'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

import { TABLE_HEADER_BG } from '~/components/common/MasterDataTable'
import { KOMA_DIVIDER_BORDER, isKomaDividerAfter } from '~/models/komaDivider'
import type { AreaDef, KomaRow } from '~/pages/SpotPrice/constants'
import {
  AREAS,
  PRICE_GROUP_COLORS,
  komaPrice,
} from '~/pages/SpotPrice/constants'

/**
 * 1 コマ分の「価格 → 色」を作る (design `bidding/spot-price.html` の `renderKomaTable`)。
 * - システムプライスと一致する価格 = 色なし (システムと同水準なので強調しない)
 * - 不一致の価格 = 同一価格ごとに昇順で色を割り当てる
 * - システムプライスが無いコマは「一致の判定ができない」ので全エリアを無地にする
 *   (適当な基準で色を割ると、分断していないコマが割れて見える)
 * 割り当ては表示中のエリアでなく全 9 エリアで行う (表示列の増減で色が変わらないように)。
 */
function buildPriceGroupColors(row: KomaRow): Map<number, string> {
  if (row.system === null) return new Map()
  const distinct = [
    ...new Set(
      AREAS.filter((a) => a.isSystem !== true)
        .map((a) => komaPrice(row, a.code))
        .filter((p): p is number => p !== null && p !== row.system),
    ),
  ].sort((x, y) => x - y)
  return new Map(
    distinct.map((p, i) => [
      p,
      PRICE_GROUP_COLORS[i % PRICE_GROUP_COLORS.length],
    ]),
  )
}

type Props = {
  komaRows: KomaRow[]
  visibleAreas: Set<string>
}

/**
 * コマ別エリア価格の表 (システムプライス一致 = 無地 / 不一致 = 同一価格ごとに同色)。
 *
 * [spec 差異] 着色は spec (`bidding/spot-price.md` 機能詳細 3. / AC-BSP-039) の
 * 「絶対価格の 5 段階ヒートマップ」ではなく、design (8080) の「同一価格 = 同色」に揃えている
 * (#7463)。どのエリアが同じ価格で約定したか (= 市場分断の有無) を一目で読むための表で、
 * 絶対値の高低は隣の価格チャートで読める。spec の更新は PdM に #9508 で依頼済み。
 *
 * 規約例外 (CLAUDE.md「一覧テーブルは共通 MasterDataTable」): 本表は CRUD マスタ一覧ではなく、
 * セルごとに価格グループで color-mix 背景 + 3px の左罫線で着色する可視化で、
 * MasterDataTable (master データ一覧向け) では per-cell の着色を表現できないため生 table を使う。
 * 同 page の StatsTable (天気/気温/統計の混在行) も同理由で生 table。色は eclair token を参照する。
 */
export const KomaTable = ({
  komaRows,
  visibleAreas,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const visibleAreaDefs = AREAS.filter((a: AreaDef) => visibleAreas.has(a.code))

  const borderColor = LightModeSemanticColors.Border.lowEmphasis.value
  const surfaceSecondary = LightModeSemanticColors.Surface.secondary.value
  const surfacePrimary = LightModeSemanticColors.Surface.primary.value
  const textMid = LightModeSemanticColors.Text.midEmphasis.value
  const textHigh = LightModeSemanticColors.Text.highEmphasis.value

  const thStyle: React.CSSProperties = {
    background: TABLE_HEADER_BG,
    padding: '6px 8px',
    borderBottom: `1px solid ${borderColor}`,
    position: 'sticky',
    top: 0,
    zIndex: 2,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.2,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    color: textHigh,
  }

  return (
    <div
      style={{
        overflow: 'auto',
        maxHeight: '70vh',
        border: `1px solid ${borderColor}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        borderRadius: 8,
        background: surfacePrimary,
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 14,
          tableLayout: 'fixed',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <colgroup>
          <col style={{ width: '3rem' }} />
          <col style={{ width: '9rem' }} />
          {visibleAreaDefs.map((a) => (
            <col
              key={a.code}
              style={{ width: a.isSystem === true ? '8rem' : undefined }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th style={thStyle}>{t('page:spotPrice.koma.colKoma')}</th>
            <th style={thStyle}>{t('page:spotPrice.koma.colTimeRange')}</th>
            {visibleAreaDefs.map((a) => (
              // エリア名はモック定数 (将来 BFF 置換) のデータ。 system 列は名称
              // 「システムプライス」が長く 8rem 列に収まらないため折返しを許可する。
              <th
                key={a.code}
                style={
                  a.isSystem === true
                    ? { ...thStyle, whiteSpace: 'normal' }
                    : thStyle
                }
              >
                {a.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {komaRows.map((pt) => {
            const isEven = pt.koma % 2 === 0
            // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)。
            // 線は各セルが持つので、 区切りの行だけセルの下線を濃くする。
            const rowBorderBottom = isKomaDividerAfter(pt.koma)
              ? KOMA_DIVIDER_BORDER
              : `1px solid ${borderColor}`
            const rowBg = isEven ? surfaceSecondary : undefined
            const groupColors = buildPriceGroupColors(pt)
            return (
              <tr key={pt.x} style={{ background: rowBg }}>
                {/* コマ列 */}
                <td
                  style={{
                    textAlign: 'center',
                    fontWeight: 600,
                    background: surfaceSecondary,
                    padding: '4px 6px',
                    borderBottom: rowBorderBottom,
                    color: textHigh,
                  }}
                >
                  {pt.koma}
                </td>
                {/* 時間帯列 */}
                <td
                  style={{
                    padding: '4px 12px',
                    textAlign: 'center',
                    color: textMid,
                    background: surfaceSecondary,
                    whiteSpace: 'nowrap',
                    borderBottom: rowBorderBottom,
                  }}
                >
                  {pt.timeRange}
                </td>
                {/* エリア価格列 */}
                {visibleAreaDefs.map((a) => {
                  // eSLJ が価格を返していない系列は null (0 にしない) = 「—」で無地
                  const price = komaPrice(pt, a.code)
                  // システムプライス列と、システムプライスと一致するセルは無地
                  const groupColor =
                    a.isSystem === true || price === null
                      ? undefined
                      : groupColors.get(price)
                  if (groupColor === undefined) {
                    return (
                      <td
                        key={a.code}
                        style={{
                          padding: '4px 6px',
                          textAlign: 'right',
                          fontWeight: 500,
                          borderBottom: rowBorderBottom,
                          color: textHigh,
                          background: rowBg,
                        }}
                      >
                        {price === null ? '—' : price.toFixed(2)}
                      </td>
                    )
                  }
                  return (
                    <td
                      key={a.code}
                      style={{
                        padding: '4px 6px',
                        textAlign: 'right',
                        fontWeight: 500,
                        borderBottom: rowBorderBottom,
                        // design HTML: `background:color-mix(in srgb,<色> 25%,var(--surface-primary));border-left:3px solid <色>`
                        background: `color-mix(in srgb, ${groupColor} 25%, ${surfacePrimary})`,
                        borderLeft: `3px solid ${groupColor}`,
                        color: textHigh,
                      }}
                    >
                      {price === null ? '—' : price.toFixed(2)}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
